import { App, Form } from "antd";
import React from "react";
import { Settings, SettingsConfig, SettingsConfigType } from "@version2/domain-package";
import EmailField from "./EmailField";
import ListField from "./ListField";
import StringField from "./StringField";
import UserKeyField from "./UserKeyField";
import KeyField from "./KeyField";
import EncryptedField from "./EncryptedField";
import CurrentUserField from "./CurrentUserField";
import type { ColProps } from "antd/es/grid/col";
import type { UserDocSelector } from "./types";

type FieldsProps = {
	settingsConfig: SettingsConfig[];
	settings: Settings[];
	onSettingsListChange: (settings: Settings[]) => void;
	className?: string;
	userDocSelector: UserDocSelector;
	labelCol?: ColProps;
	customFields?: { [key: string]: (props: FieldProps) => React.ReactElement };
};

const Fields = ({
	settingsConfig,
	settings,
	onSettingsListChange,
	className,
	userDocSelector,
	labelCol = { span: 4 },
	customFields,
}: FieldsProps) => {
	const onSettingsChange = (updatedSettings: Settings) => {
		const newSettings = [...settings];
		const index = settings.findIndex(
			(setting) =>
				setting._.settingsConfigId === updatedSettings._.settingsConfigId
		);
		if (index === -1) newSettings.push(updatedSettings);
		else newSettings[index] = updatedSettings;
		onSettingsListChange(newSettings);
	};

	return (
		<div>
			{settingsConfig.map((settingConfig) => {
				return (
					<Form.Item
						labelCol={labelCol}
						key={settingConfig._.id}
						label={settingConfig._.label}
						name={settingConfig._.id}
						extra={settingConfig._.description}
						className={className}
						// rules={
						//   settingConfig._.validators
						//     ? rules[settingConfig._.validators as string]
						//     : undefined
						// }
					>
						<Field
							settingConfig={settingConfig}
							settings={
								settings.find(
									(setting) => setting._.settingsConfigId === settingConfig._.id
								) ||
								new Settings({
									id: settingConfig._.id,
									settingsConfigId: settingConfig._.id,
									editedValue: "",
								})
							}
							onSettingsChange={onSettingsChange}
							userDocSelector={userDocSelector}
							customFields={customFields}
						/>
					</Form.Item>
				);
			})}
		</div>
	);
};

type FieldProps = {
	settingConfig: SettingsConfig;
	settings: Settings;
	onSettingsChange: (settings: Settings) => void;
	userDocSelector: UserDocSelector;
	customFields?: { [key: string]: (props: FieldProps) => React.ReactElement };
};

const Field = ({
	settingConfig,
	settings,
	onSettingsChange,
	userDocSelector,
	customFields,
}: FieldProps) => {
	const { message } = App.useApp();
	const hasPendingEdit = Object.prototype.hasOwnProperty.call(
		settings._,
		"editedValue"
	);
	if (settingConfig._.encrypted && !hasPendingEdit) {
		return (
			<EncryptedField settings={settings} onSettingsChange={onSettingsChange} />
		);
	} else if (customFields && customFields[settingConfig._.type]) {
		return customFields[settingConfig._.type]({
			settingConfig,
			settings,
			onSettingsChange,
			userDocSelector,
		});
	} else if (settingConfig._.type === SettingsConfigType.STRING) {
		return (
			<StringField
				settings={settings}
				onSettingsChange={onSettingsChange}
				data={settingConfig._.data}
			/>
		);
	} else if (settingConfig._.type === SettingsConfigType.EMAIL) {
		return (
			<EmailField settings={settings} onSettingsChange={onSettingsChange} />
		);
	} else if (settingConfig._.type === SettingsConfigType.USER_KEY) {
		return <UserKeyField userDocSelector={userDocSelector} />;
	} else if (settingConfig._.type === SettingsConfigType.KEY) {
		return <KeyField value={settings?.value} message={message} />;
	} else if (settingConfig._.type === SettingsConfigType.LIST) {
		return (
			<ListField
				settings={settings}
				settingConfig={settingConfig}
				onSettingsChange={onSettingsChange}
				userDocSelector={userDocSelector}
				customFields={customFields}
			/>
		);
	} else if (settingConfig._.type === SettingsConfigType.CURRENT_USER) {
		return (
			<CurrentUserField
				userDocSelector={userDocSelector}
				settings={settings}
				onSettingsChange={onSettingsChange}
				data={settingConfig._.data}
			/>
		);
	} else {
		return <div>Unknown type</div>;
	}
};

export default Fields;

export { Field };
export type { FieldsProps, FieldProps };
